import React, { useEffect, useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import {
  PlayCircle, FileText, CheckCircle2, XCircle, Printer, Download, ReceiptText, Mail, Inbox,
} from "lucide-react";
import { toast } from "sonner";

export const STATE_STYLES = {
  draft: "bg-[#e0f2fe] text-[#0369a1]",
  in_review: "bg-[#fef3c7] text-[#92400e]",
  approved: "bg-[#ccfbf1] text-[#0f766e]",
  issued: "bg-[#dcfce7] text-[#15803d]",
  paid: "bg-[#bbf7d0] text-[#166534]",
  credited: "bg-[#fee2e2] text-[#b91c1c]",
  void: "bg-[#e4e4e7] text-[#52525b]",
};

export function StatePill({ state }) {
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap ${STATE_STYLES[state] || "bg-secondary"}`}>
      {(state || "").replace("_", " ")}
    </span>
  );
}

export default function InvoicesTab({ period, config }) {
  const [invoices, setInvoices] = useState([]);
  const [running, setRunning] = useState(false);
  const [detail, setDetail] = useState(null);
  const [cnReason, setCnReason] = useState("");
  const [cnAmount, setCnAmount] = useState("");
  const [cnOpen, setCnOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState("");
  const [sending, setSending] = useState(false);
  const [outboxOpen, setOutboxOpen] = useState(false);
  const [outboxList, setOutboxList] = useState([]);
  const [outboxMsg, setOutboxMsg] = useState(null);

  const load = () => popsApi.invoices(period).then(setInvoices).catch(console.error);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const runAll = async () => {
    setRunning(true);
    try {
      const res = await popsApi.runInvoices(period);
      toast.success(`Invoice run complete — ${res.created} drafts created`, {
        description: res.skipped.length ? `${res.skipped.length} skipped (already issued/approved)` : "Pre-issue validation pack executed on every draft.",
      });
      load();
    } catch (e) {
      toast.error("Run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetail(await popsApi.invoice(id));
    } catch (e) {
      toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const doTransition = async (action) => {
    try {
      const updated = await popsApi.transition(detail.id, action, action === "approve" ? "marcus.gm" : "priya.billing");
      setDetail(updated);
      toast.success(`Invoice ${action.replace("_", " ")} → ${updated.state}`);
      load();
    } catch (e) {
      toast.error("Transition blocked", { description: e?.response?.data?.detail || e.message });
    }
  };

  const createCn = async () => {
    try {
      const cn = await popsApi.creditNote(detail.id, cnReason || "Correction", cnAmount ? parseFloat(cnAmount) : null);
      toast.success(`Credit note ${cn.number} issued`, { description: `Original invoice moved to CREDITED.` });
      setCnOpen(false);
      setDetail(null);
      load();
    } catch (e) {
      toast.error("Credit note failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const res = await popsApi.sendInvoice(detail.id, sendTo);
      toast.success(`Emailed to ${res.message.to}`, {
        description: res.message.status === "delivered_preview"
          ? "Delivered to outbox preview (no email provider key configured yet)."
          : `Sent via ${res.message.provider}.`,
      });
      setDetail({ ...detail, sends: res.sends });
      setSendOpen(false);
      setSendTo("");
    } catch (e) {
      toast.error("Send failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSending(false);
    }
  };

  const openOutbox = async () => {
    setOutboxOpen(true);
    setOutboxMsg(null);
    try {
      setOutboxList(await popsApi.outbox());
    } catch (e) {
      toast.error("Outbox load failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const totals = invoices.filter((i) => i.doc_type === "invoice");
  const canEmail = detail && (detail.doc_type === "credit_note" || ["issued", "paid", "credited"].includes(detail.state));

  return (
    <div className="space-y-4" data-testid="pops-invoices-tab">
      <div className="cs-card p-4 flex flex-wrap items-center gap-3">
        <ReceiptText className="w-4 h-4 text-[#00579e]" />
        <div>
          <div className="cs-heading text-sm font-semibold">Invoice run — {period}</div>
          <div className="text-[11px] text-muted-foreground">
            {totals.length} document(s) · rates one FOCUS ledger through L1 + customer price books, applies charges & credits, locks FX
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={openOutbox} data-testid="open-outbox-button"
            className="rounded-full h-9 text-xs px-4">
            <Inbox className="w-4 h-4 mr-1.5" /> Outbox
          </Button>
          <Button onClick={runAll} disabled={running} data-testid="run-invoices-button"
            className="rounded-full h-9 bg-[#00579e] hover:bg-[#004580] text-white text-xs px-5">
            <PlayCircle className="w-4 h-4 mr-1.5" /> {running ? "Rating & drafting…" : "Run Invoices (all customers)"}
          </Button>
        </div>
      </div>

      <div className="cs-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                {["Number", "Customer", "State", "Validation", "Total (USD)", "Billed", "FX", "Created", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="no-invoices">
                  No invoices for {period} yet — click "Run Invoices".
                </td></tr>
              )}
              {invoices.map((i) => (
                <tr key={i.id} className="cs-row-hover border-b border-border last:border-0 cursor-pointer"
                    data-testid={`invoice-row-${i.number}`} onClick={() => openDetail(i.id)}>
                  <td className="px-4 py-3 cs-mono text-[12px] font-semibold">
                    {i.number}
                    {i.doc_type === "credit_note" && <span className="ml-1 text-[10px] text-[#b91c1c]">(credit note → {i.ref_invoice_number})</span>}
                  </td>
                  <td className="px-4 py-3">{i.customer?.name}</td>
                  <td className="px-4 py-3"><StatePill state={i.state} /></td>
                  <td className="px-4 py-3 text-xs">
                    {i.validation?.blocking_failures > 0
                      ? <span className="text-[#b91c1c] font-semibold flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />{i.validation.blocking_failures} blocking</span>
                      : <span className="text-[#15803d] flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />pass{i.validation?.warnings ? ` · ${i.validation.warnings} warn` : ""}</span>}
                  </td>
                  <td className="px-4 py-3 text-right cs-mono font-semibold">{fmtUSD(i.totals?.total_usd)}</td>
                  <td className="px-4 py-3 text-right cs-mono">{i.totals?.billed_amount?.toLocaleString()} {i.totals?.currency}</td>
                  <td className="px-4 py-3 text-[11px] text-muted-foreground cs-mono">{i.fx?.rate}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{(i.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3"><FileText className="w-4 h-4 text-muted-foreground" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-4xl rounded-sm border-border max-h-[88vh] overflow-y-auto" data-testid="invoice-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 flex-wrap">
                  <DialogTitle className="cs-heading text-lg">{detail.number}</DialogTitle>
                  <StatePill state={detail.state} />
                  <span className="text-xs text-muted-foreground">{detail.customer?.name} · {detail.period} · prepared by {detail.prepared_by}</span>
                </div>
                <DialogDescription>
                  FX locked: 1 USD = {detail.fx?.rate} {detail.fx?.currency} · {detail.fx?.source} · config {detail.config_version}
                </DialogDescription>
              </DialogHeader>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {detail.state === "draft" && (
                  <Button size="sm" onClick={() => doTransition("submit_review")} data-testid="inv-submit-review"
                    className="rounded-full h-8 text-xs bg-[#00579e] hover:bg-[#004580] text-white">Submit for review</Button>
                )}
                {detail.state === "in_review" && (
                  <>
                    <Button size="sm" onClick={() => doTransition("approve")} data-testid="inv-approve"
                      className="rounded-full h-8 text-xs bg-[#0f766e] hover:bg-[#115e59] text-white">Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => doTransition("reject")} data-testid="inv-reject"
                      className="rounded-full h-8 text-xs">Reject → draft</Button>
                  </>
                )}
                {detail.state === "approved" && (
                  <Button size="sm" onClick={() => doTransition("issue")} data-testid="inv-issue"
                    className="rounded-full h-8 text-xs bg-[#15803d] hover:bg-[#166534] text-white">Issue (immutable)</Button>
                )}
                {detail.state === "issued" && (
                  <>
                    <Button size="sm" onClick={() => doTransition("mark_paid")} data-testid="inv-mark-paid"
                      className="rounded-full h-8 text-xs bg-[#166534] hover:bg-[#14532d] text-white">Mark paid</Button>
                    <Button size="sm" variant="outline" onClick={() => setCnOpen(true)} data-testid="inv-credit-note"
                      className="rounded-full h-8 text-xs border-[#ef4444] text-[#ef4444]">Credit note…</Button>
                  </>
                )}
                {detail.doc_type === "invoice" && (
                  <a href={`/invoice-print/${detail.id}`} target="_blank" rel="noreferrer"
                     className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-border text-xs hover:bg-[hsl(var(--cs-tint))]"
                     data-testid="inv-print-link">
                    <Printer className="w-3.5 h-3.5" /> Print / PDF
                  </a>
                )}
                {canEmail && (
                  <Button size="sm" variant="outline" onClick={() => setSendOpen(true)} data-testid="inv-send-email"
                    className="rounded-full h-8 text-xs border-[#00579e] text-[#00579e]">
                    <Mail className="w-3.5 h-3.5 mr-1" /> Email to customer
                  </Button>
                )}
                {["csv", "focus", "json"].map((f) => (
                  <a key={f} href={popsApi.exportUrl(detail.id, f)} data-testid={`inv-export-${f}`}
                     className="inline-flex items-center gap-1 px-3 h-8 rounded-full border border-border text-xs hover:bg-[hsl(var(--cs-tint))] uppercase">
                    <Download className="w-3 h-3" /> {f}
                  </a>
                ))}
              </div>

              {/* Validation pack */}
              {detail.validation?.checks?.length > 0 && (
                <div className="border border-border rounded-sm p-3" data-testid="validation-pack">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Pre-issue validation pack</div>
                  <div className="space-y-1">
                    {detail.validation.checks.map((c) => (
                      <div key={c.id} className="flex items-start gap-2 text-xs">
                        {c.passed
                          ? <CheckCircle2 className="w-3.5 h-3.5 text-[#15803d] mt-0.5 shrink-0" />
                          : <XCircle className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${c.blocking ? "text-[#b91c1c]" : "text-[#b45309]"}`} />}
                        <span className="font-medium">{c.name}</span>
                        <span className="text-muted-foreground">— {c.detail}</span>
                        {!c.passed && <span className={`ml-auto text-[10px] uppercase font-semibold ${c.blocking ? "text-[#b91c1c]" : "text-[#b45309]"}`}>{c.blocking ? "blocking" : "warning"}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lines */}
              <table className="w-full text-xs">
                <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                  <tr className="text-left">
                    {["Kind", "Description", "Qty", "Unit rate", "Amount (USD)", "MSP cost", "Margin"].map((h) => (
                      <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detail.lines.map((l, idx) => (
                    <tr key={idx} className="border-b border-border last:border-0">
                      <td className="px-3 py-2"><span className="cs-mono text-[10px] uppercase text-muted-foreground">{l.kind}</span></td>
                      <td className="px-3 py-2">
                        {l.description}
                        {l.formula && <div className="text-[10px] text-muted-foreground cs-mono">{l.formula}</div>}
                      </td>
                      <td className="px-3 py-2 text-right cs-mono">{Number(l.qty).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right cs-mono">{Number(l.unit_rate).toLocaleString(undefined, { maximumFractionDigits: 4 })}</td>
                      <td className={`px-3 py-2 text-right cs-mono font-semibold ${l.amount < 0 ? "text-[#b91c1c]" : ""}`}>{fmtUSD(l.amount)}</td>
                      <td className="px-3 py-2 text-right cs-mono text-muted-foreground">{fmtUSD(l.msp_cost)}</td>
                      <td className={`px-3 py-2 text-right cs-mono ${l.margin_usd < 0 ? "text-[#b91c1c]" : "text-[#15803d]"}`}>{fmtUSD(l.margin_usd)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-border font-semibold">
                    <td colSpan={4} className="px-3 py-2 text-right">TOTAL</td>
                    <td className="px-3 py-2 text-right cs-mono" data-testid="inv-total">{fmtUSD(detail.totals.total_usd)}</td>
                    <td className="px-3 py-2 text-right cs-mono text-muted-foreground">{fmtUSD(detail.totals.msp_cost_total_usd)}</td>
                    <td className="px-3 py-2 text-right cs-mono text-[#15803d]">{fmtUSD(detail.totals.margin_usd)} ({detail.totals.margin_pct}%)</td>
                  </tr>
                  <tr className="text-[11px] text-muted-foreground">
                    <td colSpan={7} className="px-3 py-1.5 text-right">
                      Billed to customer: <span className="cs-mono font-semibold text-foreground">{detail.totals.billed_amount?.toLocaleString()} {detail.totals.currency}</span> @ {detail.fx.rate}
                    </td>
                  </tr>
                </tfoot>
              </table>

              {/* Send history */}
              {detail.sends?.length > 0 && (
                <div className="border border-border rounded-sm p-3" data-testid="send-history">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Email send history</div>
                  {detail.sends.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs py-0.5">
                      <Mail className="w-3.5 h-3.5 text-[#00579e]" />
                      <span className="cs-mono">{s.to}</span>
                      <span className="text-muted-foreground">by {s.by} · {(s.at || "").slice(0, 16).replace("T", " ")}</span>
                      <span className={`ml-auto px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${s.status === "delivered_preview" ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#dcfce7] text-[#15803d]"}`}>
                        {s.status === "delivered_preview" ? "outbox preview" : s.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* History */}
              <div className="text-[11px] text-muted-foreground">
                {detail.history.map((h, i) => (
                  <span key={i}>{i > 0 && " → "}{h.state} ({h.actor}, {(h.at || "").slice(0, 16).replace("T", " ")})</span>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send email dialog */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="rounded-sm border-border" data-testid="send-email-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Email invoice {detail?.number}</DialogTitle>
            <DialogDescription>
              Sends the invoice (HTML) with the reconciling report attached (CSV). Leave the recipient
              empty to auto-resolve the customer's billing contact.
            </DialogDescription>
          </DialogHeader>
          <Input value={sendTo} onChange={(e) => setSendTo(e.target.value)} type="email"
            placeholder="Recipient (empty = billing contact)" data-testid="send-to-input" className="rounded-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} data-testid="send-confirm"
              className="rounded-sm bg-[#00579e] hover:bg-[#004580] text-white">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> {sending ? "Sending…" : "Send"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Outbox dialog */}
      <Dialog open={outboxOpen} onOpenChange={setOutboxOpen}>
        <DialogContent className="max-w-3xl rounded-sm border-border max-h-[85vh] overflow-y-auto" data-testid="outbox-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Email outbox</DialogTitle>
            <DialogDescription>
              Every invoice email with its rendered body and attachment. Messages stay in preview until an
              email provider key is configured.
            </DialogDescription>
          </DialogHeader>
          {!outboxMsg ? (
            <table className="w-full text-xs">
              <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
                <tr className="text-left">
                  {["Invoice", "To", "Status", "Sent", ""].map((h) => (
                    <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {outboxList.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground" data-testid="outbox-empty">No emails sent yet.</td></tr>
                )}
                {outboxList.map((m) => (
                  <tr key={m.id} className="border-b border-border last:border-0" data-testid={`outbox-row-${m.id}`}>
                    <td className="px-3 py-2 cs-mono font-semibold">{m.invoice_number}</td>
                    <td className="px-3 py-2">{m.to}</td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${m.status === "delivered_preview" ? "bg-[#fef3c7] text-[#92400e]" : "bg-[#dcfce7] text-[#15803d]"}`}>
                        {m.status === "delivered_preview" ? "preview" : m.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{(m.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => popsApi.outboxMsg(m.id).then(setOutboxMsg)} data-testid={`outbox-view-${m.id}`}
                        className="text-[11px] text-[#00579e] font-semibold hover:underline">view</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="space-y-3" data-testid="outbox-preview">
              <button onClick={() => setOutboxMsg(null)} className="text-[11px] text-[#00579e] hover:underline">← back to outbox</button>
              <div className="text-xs">
                <div><span className="text-muted-foreground">To:</span> <span className="cs-mono">{outboxMsg.to}</span></div>
                <div><span className="text-muted-foreground">Subject:</span> {outboxMsg.subject}</div>
                <div><span className="text-muted-foreground">Attachment:</span> <span className="cs-mono">{outboxMsg.attachment?.filename}</span></div>
              </div>
              <iframe title="email-preview" srcDoc={outboxMsg.html} className="w-full h-[420px] border border-border rounded-sm bg-white" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Credit note dialog */}
      <Dialog open={cnOpen} onOpenChange={setCnOpen}>
        <DialogContent className="rounded-sm border-border" data-testid="credit-note-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Issue credit note</DialogTitle>
            <DialogDescription>Issued invoices are immutable — corrections flow through a referenced credit note.</DialogDescription>
          </DialogHeader>
          <Input value={cnReason} onChange={(e) => setCnReason(e.target.value)} placeholder="Reason (e.g., provider restatement true-up)"
            data-testid="cn-reason-input" className="rounded-sm" />
          <Input value={cnAmount} onChange={(e) => setCnAmount(e.target.value)} type="number" placeholder="Amount USD (empty = full)"
            data-testid="cn-amount-input" className="rounded-sm" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCnOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={createCn} data-testid="cn-confirm" className="rounded-sm bg-[#ef4444] hover:bg-[#dc2626] text-white">Issue credit note</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
